import { type CellValueChangedEvent } from "ag-grid-community";

const REVERT_EVENT_SOURCE = "revert-on-error";

export type UseRevertEditOnErrorReturn<TData> = {
  /**
   * Wrap this around the onCellValueChanged callback passed to DataGrid. The
   * wrapped handler receives the change event plus a `revertEdit` bound to that
   * specific edit — call it (e.g. from a mutation's onError) to roll the cell
   * back to its previous value. Each invocation gets its own `revertEdit`, so
   * concurrent edits to different cells revert independently.
   */
  captureCellChangeEvent: (
    onCellValueChanged: (
      event: CellValueChangedEvent<TData>,
      revertEdit: () => void,
    ) => void,
  ) => (event: CellValueChangedEvent<TData>) => void;
};

export function useRevertEditOnError<
  TData,
>(): UseRevertEditOnErrorReturn<TData> {
  const captureCellChangeEvent =
    (
      onCellValueChanged: (
        event: CellValueChangedEvent<TData>,
        revertEdit: () => void,
      ) => void,
    ) =>
    (event: CellValueChangedEvent<TData>) => {
      if (event.source === REVERT_EVENT_SOURCE) return;

      const revertEdit = () => {
        event.node.setDataValue(
          event.column.getColId(),
          event.oldValue ?? "",
          REVERT_EVENT_SOURCE,
        );
      };

      onCellValueChanged(event, revertEdit);
    };

  return { captureCellChangeEvent };
}
